import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCategoriaCampos } from '@/hooks/useCategoriaCampos';
import { CategoriaCampoFormData, TipoCampoCat } from '@/types/categoria-campo';
import { Plus, X } from 'lucide-react';

const campoSchema = z.object({
  nome_campo: z.string().min(1, 'Nome do campo é obrigatório').max(100),
  label: z.string().min(1, 'Label é obrigatório').max(200),
  tipo_campo: z.enum(['text', 'textarea', 'number', 'date', 'boolean', 'select', 'multiselect', 'email', 'url']),
  descricao: z.string().optional(),
  obrigatorio: z.boolean(),
  ordem: z.number().min(0),
  ativo: z.boolean(),
});

interface NewCampoCategoriaDialogProps {
  categoriaId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const NewCampoCategoriaDialog = ({
  categoriaId,
  isOpen,
  onClose,
}: NewCampoCategoriaDialogProps) => {
  const { createCampo, campos } = useCategoriaCampos(categoriaId);
  const [opcoes, setOpcoes] = useState<string[]>([]);
  const [novaOpcao, setNovaOpcao] = useState('');

  const form = useForm<CategoriaCampoFormData>({
    resolver: zodResolver(campoSchema),
    defaultValues: {
      nome_campo: '',
      label: '',
      tipo_campo: 'text',
      descricao: '',
      obrigatorio: false,
      ordem: campos.length,
      ativo: true,
    },
  });

  const tipoCampo = form.watch('tipo_campo');

  const handleAddOpcao = () => {
    if (novaOpcao.trim()) {
      setOpcoes([...opcoes, novaOpcao.trim()]);
      setNovaOpcao('');
    }
  };

  const handleRemoveOpcao = (index: number) => {
    setOpcoes(opcoes.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: CategoriaCampoFormData) => {
    try {
      const configuracoes: any = {};
      
      if (tipoCampo === 'select' || tipoCampo === 'multiselect') {
        configuracoes.opcoes = opcoes;
      }

      await createCampo.mutateAsync({
        ...data,
        categoria_id: categoriaId,
        configuracoes: Object.keys(configuracoes).length > 0 ? configuracoes : undefined,
      });
      
      form.reset();
      setOpcoes([]);
      onClose();
    } catch (error) {
      console.error('Erro ao criar campo:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Campo Customizado</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label do Campo *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Localização do Problema" {...field} />
                  </FormControl>
                  <FormDescription>Nome que aparecerá no formulário</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nome_campo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Interno *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: localizacao_problema" {...field} />
                  </FormControl>
                  <FormDescription>Nome técnico (sem espaços ou caracteres especiais)</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tipo_campo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Campo *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="text">Texto</SelectItem>
                      <SelectItem value="textarea">Texto Longo</SelectItem>
                      <SelectItem value="number">Número</SelectItem>
                      <SelectItem value="date">Data</SelectItem>
                      <SelectItem value="boolean">Sim/Não</SelectItem>
                      <SelectItem value="select">Seleção</SelectItem>
                      <SelectItem value="multiselect">Seleção Múltipla</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="url">URL</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {(tipoCampo === 'select' || tipoCampo === 'multiselect') && (
              <div className="space-y-2">
                <FormLabel>Opções</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="Digite uma opção"
                    value={novaOpcao}
                    onChange={(e) => setNovaOpcao(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOpcao())}
                  />
                  <Button type="button" onClick={handleAddOpcao} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {opcoes.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {opcoes.map((opcao, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                        <span className="text-sm">{opcao}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOpcao(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Descrição opcional do campo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="obrigatorio"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Campo Obrigatório</FormLabel>
                    <FormDescription>
                      Usuário precisa preencher este campo
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ordem"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ordem de Exibição</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createCampo.isPending}>
                {createCampo.isPending ? 'Criando...' : 'Criar Campo'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
